/**
 * #1134 — wires `accountMarketIdentity` to the account it belongs to.
 *
 * Scope follows the signed-in user + organization. The named tickers are the
 * Kalshi tickers the account store holds right now — the fills tape, recent
 * fills and positions (a settled position stays listed until the relay's sweep
 * catches up, which is exactly when its live row is already gone) — narrowed
 * to those whose label can take its pair from producer identity
 * (`pairComesFromProducer`), since no other label ever reads a kept pair.
 *
 * On a scope change the account store still holds the PREVIOUS account's rows
 * until `useKalshiAccount`'s cleanup disconnects it, so nothing is named until
 * the store has emptied: the old account's tickers never enter the new list.
 *
 * Started and stopped with the market feed (`useHydra`), so it runs in the
 * main shell and in the standalone /fills popup alike — the two hosts that
 * mount the label renderers — and the pending write is flushed on stop. The
 * release after a switch relies on `useKalshiAccount`, which disconnects on
 * every input the scope reads (status, user id, organization).
 */
import { useAccountStore } from './accountStore';
import { useAuthStore } from './authStore';
import { useMarketStore } from './marketStore';
import { pairComesFromProducer } from '@/lib/kalshiTicker/describeMarket';
import {
  flushPersist,
  noteRelevantTickers,
  setIdentityScope,
  invalidateHistoricalIdentitySession,
  type LiveIdentity,
} from './accountMarketIdentity';

let _unsubscribe: (() => void) | null = null;
let _awaitingAccountReset = false;

function scopeOf(): string | null {
  const { status, user } = useAuthStore.getState();
  if (status !== 'authenticated' || !user) return null;
  return `${user.id}|${user.orgId ?? ''}`;
}

function accountTickers(): string[] {
  const { activityTicker, recentFills, positions } = useAccountStore.getState();
  const out: string[] = [];
  for (const f of activityTicker) out.push(f.ticker);
  for (const f of recentFills) out.push(f.ticker);
  for (const p of positions) out.push(p.ticker);
  return out;
}

function readLive(ticker: string): LiveIdentity | null {
  return useMarketStore.getState().getMarket(ticker);
}

function nameAccountTickers(): void {
  noteRelevantTickers(accountTickers().filter(pairComesFromProducer), readLive);
}

export function startAccountIdentityRetention(): void {
  if (_unsubscribe) return;
  setIdentityScope(scopeOf());
  _awaitingAccountReset = false;
  nameAccountTickers();
  const offAuth = useAuthStore.subscribe((s, prev) => {
    if (s.status === prev.status && s.user?.id === prev.user?.id && s.user?.orgId === prev.user?.orgId
        && s.user?.role === prev.user?.role) return;
    invalidateHistoricalIdentitySession();
    if (!setIdentityScope(scopeOf())) return;
    _awaitingAccountReset = accountTickers().length > 0;
    if (!_awaitingAccountReset) nameAccountTickers();
  });
  const offAccount = useAccountStore.subscribe((s, prev) => {
    if (s.keyMode !== prev.keyMode) invalidateHistoricalIdentitySession();
    if (s.activityTicker === prev.activityTicker && s.recentFills === prev.recentFills && s.positions === prev.positions) return;
    if (_awaitingAccountReset) {
      if (accountTickers().length > 0) return;
      _awaitingAccountReset = false;
    }
    nameAccountTickers();
  });
  _unsubscribe = () => {
    offAuth();
    offAccount();
  };
}

export function stopAccountIdentityRetention(): void {
  _unsubscribe?.();
  _unsubscribe = null;
  _awaitingAccountReset = false;
  flushPersist();
}
